import React from 'react';
import { Image as ImageIcon, Link2, Play } from 'lucide-react';
import { Modal } from '../../UI/Modal';
import CommentThreadSection from '../../UI/CommentThreadSection';
import type { WorkComment } from '../../UI/VideoModal';

interface ExampleWorkView {
    type?: 'video' | 'image';
    thumbnailUrl?: string;
    videoUrl?: string;
    images?: string[];
    title: string;
    description?: string;
    sourceUrl?: string;
    commentThreadId?: string;
    ownerUserId?: string;
    comments?: WorkComment[];
    commentCount?: number;
}

interface ExampleWorkViewModalProps {
    isOpen: boolean;
    work: ExampleWorkView | null;
    onClose: () => void;
    zIndex?: number;
}

const ExampleWorkViewModal: React.FC<ExampleWorkViewModalProps> = ({
    isOpen,
    work,
    onClose,
    zIndex = 90
}) => {
    if (!isOpen || !work) {
        return null;
    }

    const previewImageUrl = work.thumbnailUrl || work.images?.[0] || '';
    const showImagePreview = !!previewImageUrl;
    const showVideoFallback = work.type === 'video' && !!work.videoUrl && !showImagePreview;

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="示例作品详情"
            size="lg"
            zIndex={zIndex}
            footer={(
                <button
                    type="button"
                    onClick={onClose}
                    className="px-5 py-2 rounded-full text-sm font-medium bg-black text-white hover:bg-gray-900 transition-colors"
                >
                    关闭
                </button>
            )}
        >
            <div className="space-y-4">
                <div className="overflow-hidden rounded-2xl border border-gray-200 bg-gray-100">
                    <div className="relative aspect-[16/9] w-full">
                        {showImagePreview ? (
                            <img
                                src={previewImageUrl}
                                alt={work.title || '示例作品预览'}
                                className="h-full w-full object-cover"
                            />
                        ) : showVideoFallback ? (
                            <video
                                src={work.videoUrl}
                                muted
                                playsInline
                                preload="metadata"
                                className="h-full w-full object-cover"
                            />
                        ) : (
                            <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-blue-50 via-white to-slate-100">
                                <div className="flex items-center gap-2 rounded-full bg-white/90 px-4 py-2 text-sm font-medium text-gray-500 shadow-sm">
                                    {work.type === 'video' ? (
                                        <Play className="h-4 w-4" />
                                    ) : work.sourceUrl ? (
                                        <Link2 className="h-4 w-4" />
                                    ) : (
                                        <ImageIcon className="h-4 w-4" />
                                    )}
                                    <span>暂无预览</span>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">标题</label>
                    <div className="w-full px-4 py-3 border border-gray-200 rounded-xl bg-white text-gray-900">
                        {work.title || '未命名示例作品'}
                    </div>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">描述</label>
                    <div className="min-h-[96px] w-full px-4 py-3 border border-gray-200 rounded-xl bg-white text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                        {work.description?.trim() || '暂无描述'}
                    </div>
                </div>

                {work.sourceUrl && (
                    <div className="rounded-xl border border-blue-100 bg-blue-50/70 px-4 py-3">
                        <p className="text-[11px] font-bold uppercase tracking-wider text-blue-500 mb-1">参考链接</p>
                        <a
                            href={work.sourceUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="text-sm text-blue-700 break-all hover:underline"
                        >
                            {work.sourceUrl}
                        </a>
                    </div>
                )}

                <div className="rounded-2xl border border-gray-100 overflow-hidden">
                    <CommentThreadSection
                        threadId={work.commentThreadId}
                        initialComments={work.comments || []}
                        initialCommentCount={work.commentCount}
                        commentsEnabled={!!work.commentThreadId}
                        commentPlaceholder="发表评论..."
                        threadOwnerUserId={work.ownerUserId}
                    />
                </div>
            </div>
        </Modal>
    );
};

export default ExampleWorkViewModal;
